import React, { useEffect, useMemo, useState } from 'react';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import { PersonAvatar } from '../../common/avatar/PersonAvatar';
import { MemberRoleBadges } from '../../common/chat/members/MemberListSectionParts';
import '../../../css/admin/chats/EventDerivedGroupModal.css';

const TypedPersonAvatar = PersonAvatar as React.ComponentType<any>;

type EventChatArtist = {
  key?: string;
  name?: string;
  partLabel?: string;
  eventPartLabel?: string;
  mainPartLabel?: string;
  allPerformancesGroupLabel?: string;
  selectionPartLabel?: string;
  avatarUrl?: string;
  pic?: string;
  gender?: string;
  initial?: string;
  isChief?: boolean;
  isConcertmaster?: boolean;
  customRoles?: Array<{ id?: string; name?: string; role_name?: string }>;
  payload?: { artist_id?: string; id?: string; artist_name?: string; name?: string };
  artist_id?: string;
};

type EventDerivedGroupModalProps = {
  open: boolean;
  eventName?: string;
  artists?: EventChatArtist[];
  saving?: boolean;
  onClose: () => void;
  onCreate?: (input: { title: string; artistIds: string[] }) => void | Promise<void>;
};

const artistIdOf = (artist: EventChatArtist) => String(
  artist?.payload?.artist_id || artist?.payload?.id || artist?.artist_id || ''
).trim();

const artistPartOf = (artist: EventChatArtist) => String(
  artist?.selectionPartLabel
  || artist?.partLabel
  || artist?.mainPartLabel
  || artist?.allPerformancesGroupLabel
  || artist?.eventPartLabel
  || 'パート未設定'
).trim() || 'パート未設定';

type SelectionGroup = { key: string; label: string; artistIds: string[] };

const appendToSelectionGroup = (
  groups: Map<string, SelectionGroup>,
  key: string,
  label: string,
  artistId: string,
) => {
  if (!artistId) return;
  if (!groups.has(key)) groups.set(key, { key, label, artistIds: [] });
  const group = groups.get(key);
  if (group && !group.artistIds.includes(artistId)) group.artistIds.push(artistId);
};

export default function EventDerivedGroupModal({
  open,
  eventName,
  artists = [],
  saving = false,
  onClose,
  onCreate,
}: EventDerivedGroupModalProps) {
  const [step, setStep] = useState('members');
  const [keyword, setKeyword] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [title, setTitle] = useState('');

  useEffect(() => {
    if (!open) return;
    setStep('members');
    setKeyword('');
    setSelectedIds(new Set());
    setTitle('');
  }, [open, eventName]);

  const visibleArtists = useMemo(() => {
    const query = keyword.trim().toLocaleLowerCase('ja');
    if (!query) return artists;
    return artists.filter((artist) => [
      artist?.name,
      artist?.partLabel,
      artist?.eventPartLabel,
      artist?.payload?.artist_name,
      artist?.payload?.name,
    ].some((value) => String(value || '').toLocaleLowerCase('ja').includes(query)));
  }, [artists, keyword]);

  const selectedArtists = useMemo(
    () => artists.filter((artist) => selectedIds.has(artistIdOf(artist))),
    [artists, selectedIds]
  );

  const partSelectionGroups = useMemo(() => {
    const groups = new Map<string, SelectionGroup>();
    artists.forEach((artist) => {
      const label = artistPartOf(artist);
      appendToSelectionGroup(groups, `part:${label}`, label, artistIdOf(artist));
    });
    return Array.from(groups.values());
  }, [artists]);

  const roleSelectionGroups = useMemo(() => {
    const groups = new Map<string, SelectionGroup>([
      ['role:concertmaster', { key: 'role:concertmaster', label: 'コンサートマスター', artistIds: [] }],
      ['role:chief', { key: 'role:chief', label: '首席', artistIds: [] }],
    ]);
    artists.forEach((artist) => {
      const artistId = artistIdOf(artist);
      if (artist?.isConcertmaster) {
        appendToSelectionGroup(groups, 'role:concertmaster', 'コンサートマスター', artistId);
      }
      if (artist?.isChief) appendToSelectionGroup(groups, 'role:chief', '首席', artistId);
      (Array.isArray(artist?.customRoles) ? artist.customRoles : []).forEach((role) => {
        const label = String(role?.name || role?.role_name || '役職').trim() || '役職';
        const key = String(role?.id || label).trim();
        appendToSelectionGroup(groups, `role:${key}`, label, artistId);
      });
    });
    return Array.from(groups.values()).filter((group) => group.artistIds.length > 0);
  }, [artists]);

  const toggleArtist = (artist: EventChatArtist) => {
    const artistId = artistIdOf(artist);
    if (!artistId) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(artistId)) next.delete(artistId);
      else next.add(artistId);
      return next;
    });
  };

  const toggleSelectionGroup = (artistIds: string[]) => {
    const normalizedIds = Array.from(new Set(artistIds.filter(Boolean)));
    if (normalizedIds.length === 0) return;
    setSelectedIds((current) => {
      const next = new Set(current);
      const allSelected = normalizedIds.every((artistId) => next.has(artistId));
      normalizedIds.forEach((artistId) => {
        if (allSelected) next.delete(artistId);
        else next.add(artistId);
      });
      return next;
    });
  };

  const renderSelectionGroups = (label: string, groups: SelectionGroup[]) => {
    if (groups.length === 0) return null;
    return (
      <section className="event-derived-group-modal__bulk-section" aria-label={label}>
        <h4>{label}</h4>
        <div className="event-derived-group-modal__bulk-options">
          {groups.map((group) => {
            const selected = group.artistIds.every((artistId) => selectedIds.has(artistId));
            return (
              <button
                key={group.key}
                type="button"
                className={selected ? 'is-selected' : ''}
                onClick={() => toggleSelectionGroup(group.artistIds)}
                aria-pressed={selected}
              >
                <span>{group.label}</span>
                <small>{group.artistIds.length}人</small>
              </button>
            );
          })}
        </div>
      </section>
    );
  };

  const submit = async () => {
    const normalizedTitle = title.trim();
    if (!normalizedTitle || selectedIds.size === 0) return;
    await onCreate?.({
      title: normalizedTitle,
      artistIds: Array.from(selectedIds),
    });
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      dismissible={!saving}
      title={step === 'members' ? 'グループチャットメンバーを選択' : 'グループ名を決める'}
      ariaLabel={step === 'members' ? 'グループチャットメンバーを選択' : 'グループ名を決める'}
      size="md"
      className="event-derived-group-modal"
      footer={step === 'members' ? (
        <>
          <DsButton
            type="button"
            onClick={() => setStep('name')}
            disabled={selectedIds.size === 0}
          >
            次へ
          </DsButton>
        </>
      ) : (
        <>
          <DsButton type="button" variant="secondary" onClick={() => setStep('members')} disabled={saving}>
            戻る
          </DsButton>
          <DsButton type="button" onClick={submit} loading={saving} disabled={!title.trim() || saving}>
            グループを作成
          </DsButton>
        </>
      )}
    >
      {step === 'members' ? (
        <div className="event-derived-group-modal__members">
          <div className="event-derived-group-modal__summary">
            <span>{eventName || 'イベント名未設定'}</span>
            <span>選択 {selectedIds.size}人</span>
          </div>
          <input
            type="search"
            value={keyword}
            onChange={(event) => setKeyword(event.target.value)}
            placeholder="名前・パートで絞り込み"
            aria-label="イベントチャットメンバーを絞り込み"
          />
          <div className="event-derived-group-modal__bulk-select">
            {renderSelectionGroups('パートで選択', partSelectionGroups)}
            {renderSelectionGroups('バッジで選択', roleSelectionGroups)}
          </div>
          <div className="event-derived-group-modal__member-list">
            {visibleArtists.length > 0 ? visibleArtists.map((artist) => {
              const artistId = artistIdOf(artist);
              const selected = selectedIds.has(artistId);
              return (
                <button
                  key={artistId || artist?.key}
                  type="button"
                  className={selected ? 'is-selected' : ''}
                  onClick={() => toggleArtist(artist)}
                  aria-pressed={selected}
                >
                  <TypedPersonAvatar
                    role="artist"
                    name={artist?.name || '名称未設定'}
                    gender={artist?.gender}
                    profilePicture={artist?.pic}
                    imageUrl={artist?.avatarUrl}
                    size={40}
                    className="event-derived-group-modal__avatar"
                  />
                  <span className="event-derived-group-modal__identity">
                    <MemberRoleBadges
                      member={artist}
                      badgesClassName="event-derived-group-modal__badges"
                      concertmasterBadgeClassName="event-derived-group-modal__badge event-derived-group-modal__badge--concertmaster"
                      chiefBadgeClassName="event-derived-group-modal__badge event-derived-group-modal__badge--chief"
                      roleBadgeClassName="event-derived-group-modal__badge event-derived-group-modal__badge--role"
                    />
                    <strong>{artist?.name || '名称未設定'}</strong>
                    <small>{artistPartOf(artist)}</small>
                  </span>
                  <i className={`fa-solid ${selected ? 'fa-circle-check' : 'fa-circle'}`} aria-hidden="true" />
                </button>
              );
            }) : (
              <p className="event-derived-group-modal__empty">選択できるイベントチャットメンバーがいません。</p>
            )}
          </div>
        </div>
      ) : (
        <div className="event-derived-group-modal__name-step">
          <label htmlFor="event-derived-group-title">グループ名</label>
          <input
            id="event-derived-group-title"
            type="text"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            maxLength={80}
            autoFocus
            placeholder="グループ名を入力"
          />
          <p className="event-derived-group-modal__linked-event">
            連動イベント: {eventName || 'イベント名未設定'}
          </p>
          <div className="event-derived-group-modal__selected-members">
            {selectedArtists.map((artist) => (
              <span key={artistIdOf(artist)}>{artist?.name || '名称未設定'}</span>
            ))}
          </div>
        </div>
      )}
    </DsModal>
  );
}
